import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Sparkles, RotateCcw, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAiQuota } from '@/lib/useAiQuota';
import { AiQuotaInline } from '@/components/AiQuotaInline';

export function GeneratorTab({ selectedOpportunity, onArticleGenerated }) {
  const { user } = useAuth();
  const { language: lang } = useLanguage();
  const isAdmin = ['admin', 'admin_enterprise'].includes(user?.role);
  const [title, setTitle] = useState(selectedOpportunity?.title || '');
  const [keyword, setKeyword] = useState(selectedOpportunity?.keyword || '');
  const [generating, setGenerating] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState(null);
  const [progress, setProgress] = useState(0);
  const { quota, refresh: refreshQuota, isReached } = useAiQuota();

  const t = (fr, en) => lang === 'fr' ? fr : en;

  // Friendly toast when backend returns the structured ai_quota_reached payload.
  const toastQuotaOr = (err, fallback) => {
    const d = err?.response?.data?.detail;
    if (d && typeof d === 'object' && d.error === 'ai_quota_reached') {
      toast.error(t(
        'Limite IA atteinte pour ce mois. Passez au forfait supérieur ou contactez l\'administrateur.',
        'AI limit reached for this month. Upgrade your plan or contact your administrator.'
      ));
      refreshQuota();
      return;
    }
    toast.error((typeof d === 'string' ? d : null) || fallback);
  };

  // Sync with selected opportunity
  useEffect(() => {
    if (selectedOpportunity) {
      setTitle(selectedOpportunity.title || '');
      setKeyword(selectedOpportunity.keyword || '');
      setGeneratedArticle(null);
    }
  }, [selectedOpportunity]);

  const generate = async () => {
    if (!title && !keyword) {
      toast.error(t('Titre ou mot-cle requis','Title or keyword required'));
      return;
    }
    setGenerating(true);
    setProgress(0);
    setGeneratedArticle(null);

    // Simulate progress
    const interval = setInterval(() => {
      setProgress(p => Math.min(p + Math.random() * 15, 90));
    }, 800);

    try {
      const payload = { title, keyword };
      if (selectedOpportunity?.opportunity_id) {
        payload.opportunity_id = selectedOpportunity.opportunity_id;
      }
      const { data } = await api.post('/seo/articles/generate', payload);
      setGeneratedArticle(data);
      setProgress(100);
      toast.success(t('Article genere!','Article generated!'));
      if (onArticleGenerated) onArticleGenerated(data);
    } catch (e) {
      // Cloudflare's 100 s edge timeout can return 5xx / network errors
      // EVEN WHEN the article was successfully saved server-side (long
      // LLM call). Before showing "Erreur de generation", poll the
      // articles list for a brand-new pending_approval article that
      // matches the current keyword/title — if one shows up within a
      // few seconds, treat the request as a success.
      const looksLikeTimeout = (
        !e.response ||
        e.response.status === 504 ||
        e.response.status === 524 ||
        e.response.status === 502 ||
        e.response.status === 499 ||
        e.code === 'ECONNABORTED'
      );
      let recovered = null;
      if (looksLikeTimeout) {
        const wantedKw = (keyword || title || '').trim().toLowerCase();
        const cutoff = Date.now() - 5 * 60 * 1000; // last 5 min
        for (let attempt = 0; attempt < 6 && !recovered; attempt++) {
          try {
            const { data: list } = await api.get('/seo/articles', { params: { limit: 20 } });
            const arts = Array.isArray(list) ? list : (list?.articles || []);
            recovered = arts.find(a => {
              if (!a) return false;
              const created = a.created_at ? new Date(a.created_at).getTime() : 0;
              if (!created || created < cutoff) return false;
              const akw = (a.keyword || a.title || '').toLowerCase();
              return wantedKw && akw && (akw.includes(wantedKw) || wantedKw.includes(akw));
            });
          } catch (_) { /* ignore polling errors */ }
          if (!recovered) await new Promise(r => setTimeout(r, 2500));
        }
      }
      if (recovered) {
        setGeneratedArticle(recovered);
        setProgress(100);
        toast.success(t('Article genere!','Article generated!'));
        if (onArticleGenerated) onArticleGenerated(recovered);
      } else {
        const detail = e.response?.data?.detail;
        const msg = typeof detail === 'string'
          ? detail
          : (detail?.message || t('Erreur de generation','Generation error'));
        toast.error(msg);
      }
    } finally {
      clearInterval(interval);
      setGenerating(false);
    }
  };

  if (!isAdmin) {
    return (
      <Card><CardContent className="py-12 text-center text-muted-foreground">
        <p>{t('Seul l\'admin peut generer des articles','Only admin can generate articles')}</p>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="seo-generator-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {t('Generateur d\'articles SEO','SEO Article Generator')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {selectedOpportunity && (
            <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 text-sm">
              <span className="font-medium">{t('Opportunite selectionnee','Selected opportunity')}: </span>
              {selectedOpportunity.title}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('Titre de l\'article','Article title')}</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={t('Ex: Drain francais a Longueuil','Ex: French drain in Longueuil')} data-testid="article-title-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('Mot-cle principal','Primary keyword')}</Label>
              <Input value={keyword} onChange={e => setKeyword(e.target.value)} placeholder={t('Ex: drain francais longueuil','Ex: french drain longueuil')} data-testid="article-keyword-input" />
            </div>
          </div>

          {generating && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('Generation en cours avec Claude AI...','Generating with Claude AI...')}</span>
                <span className="font-medium">{Math.round(progress)}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <AiQuotaInline quota={quota} />
            <div className="flex gap-3">
              <Button onClick={generate} disabled={generating || isReached || (!title && !keyword)} data-testid="generate-article-btn">
                {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                {isReached ? t('Quota atteint','Quota reached') : (generating ? t('Generation...','Generating...') : t('Generer l\'article','Generate article'))}
              </Button>
              {generatedArticle && (
                <Button variant="outline" onClick={() => { setGeneratedArticle(null); setProgress(0); generate(); }} disabled={isReached}>
                  <RotateCcw className="mr-2 h-4 w-4" />{t('Regenerer','Regenerate')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {generatedArticle && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('Apercu de l\'article','Article preview')}
              </CardTitle>
              <div className="text-sm text-muted-foreground">{generatedArticle.word_count} {t('mots','words')}</div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">H1</p>
              <h2 className="text-xl font-bold">{generatedArticle.title}</h2>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Meta description</p>
              <p className="text-sm italic border-l-2 border-primary pl-3">{generatedArticle.meta_description}</p>
            </div>
            <div className="border rounded-lg p-4 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(generatedArticle.content || '') }} />
            {generatedArticle.faq?.length > 0 && (
              <div>
                <p className="font-semibold mb-2">FAQ ({generatedArticle.faq.length})</p>
                <div className="space-y-2">
                  {generatedArticle.faq.map((f, i) => (
                    <div key={`faq-${f.question?.slice(0,32) || i}`} className="bg-muted/50 rounded p-3">
                      <p className="font-medium text-sm">{f.question}</p>
                      <p className="text-sm text-muted-foreground mt-1">{f.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground italic">
              {t('Statut: En attente d\'approbation','Status: Pending approval')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
